
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateResponse} from './ConfigUpdateResponse';
import {FieldModelConfigUpdateResponse} from './ConfigUpdateResponse';
import {SymbolGroup} from '../core/SymbolGroup';
import {FieldModelSymbolGroup} from '../core/SymbolGroup';

/**
 * SymbolGroupDeleteResponse struct
 */
class SymbolGroupDeleteResponse extends ConfigUpdateResponse {
  /**
   * Initialize struct
   * @param {!ConfigUpdateResponse=} parent
   * @param {!SymbolGroup=} symbolGroup
   * @constructor
   */
  constructor (parent = new ConfigUpdateResponse(), symbolGroup = new SymbolGroup()) {
    super()
    super.copy(parent)
    this.SymbolGroup = symbolGroup
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SymbolGroupDeleteResponse}
   * @param {!SymbolGroupDeleteResponse} other Other struct
   * @returns {!SymbolGroupDeleteResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.SymbolGroup != null) {
      this.SymbolGroup = SymbolGroup.fromObject(other.SymbolGroup)
    } else {
      this.SymbolGroup = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SymbolGroupDeleteResponse}
   * @returns {!SymbolGroupDeleteResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SymbolGroupDeleteResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SymbolGroupDeleteResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SymbolGroupDeleteResponse}
   * @param {!SymbolGroupDeleteResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SymbolGroupDeleteResponse)) {
      throw new TypeError('Instance of SymbolGroupDeleteResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SymbolGroupDeleteResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      SymbolGroup: ((this.SymbolGroup != null) ? this.SymbolGroup : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SymbolGroupDeleteResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SymbolGroupDeleteResponse} other Object value
   * @returns {!SymbolGroupDeleteResponse} Created struct
   */
  static fromObject (other) {
    return new SymbolGroupDeleteResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SymbolGroupDeleteResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SymbolGroupDeleteResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SymbolGroupDeleteResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6105
  }
}

export { SymbolGroupDeleteResponse };

/**
 * Fast Binary Encoding SymbolGroupDeleteResponse field model
 */
class FieldModelSymbolGroupDeleteResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateResponse(buffer, 4 + 4)
    this.SymbolGroup = new FieldModelSymbolGroup(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
  }

  /**
   * Get the ConfigUpdateResponse field model
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @returns {!FieldModelConfigUpdateResponse} ConfigUpdateResponse field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.SymbolGroup.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.SymbolGroup.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSymbolGroupDeleteResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6105
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SymbolGroup.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolGroup.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolGroup.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @param {!SymbolGroupDeleteResponse} fbeValue Default value, defaults is new SymbolGroupDeleteResponse()
   * @returns {!SymbolGroupDeleteResponse} SymbolGroupDeleteResponse value
   */
  get (fbeValue = new SymbolGroupDeleteResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @param {!SymbolGroupDeleteResponse} fbeValue SymbolGroupDeleteResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SymbolGroup.fbeSize) <= fbeStructSize) {
      fbeValue.SymbolGroup = this.SymbolGroup.get()
    } else {
      fbeValue.SymbolGroup = new SymbolGroup()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolGroup.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @param {!SymbolGroupDeleteResponse} fbeValue SymbolGroupDeleteResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSymbolGroupDeleteResponse}
   * @param {SymbolGroupDeleteResponse} fbeValue SymbolGroupDeleteResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.SymbolGroup.set(fbeValue.SymbolGroup)
  }
}

export { FieldModelSymbolGroupDeleteResponse };

/**
 * Fast Binary Encoding SymbolGroupDeleteResponse model
 */
class SymbolGroupDeleteResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSymbolGroupDeleteResponse(this.buffer, 4)
  }

  /**
   * Get the SymbolGroupDeleteResponse model
   * @this {!SymbolGroupDeleteResponseModel}
   * @returns {!FieldModelSymbolGroupDeleteResponse} model SymbolGroupDeleteResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SymbolGroupDeleteResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SymbolGroupDeleteResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SymbolGroupDeleteResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SymbolGroupDeleteResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSymbolGroupDeleteResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SymbolGroupDeleteResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SymbolGroupDeleteResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SymbolGroupDeleteResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SymbolGroupDeleteResponseModel}
   * @param {!SymbolGroupDeleteResponse} value SymbolGroupDeleteResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SymbolGroupDeleteResponseModel}
   * @param {!SymbolGroupDeleteResponse} value SymbolGroupDeleteResponse value, defaults is new SymbolGroupDeleteResponse()
   * @return {!object} Deserialized SymbolGroupDeleteResponse value and its size
   */
  deserialize (value = new SymbolGroupDeleteResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SymbolGroupDeleteResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SymbolGroupDeleteResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SymbolGroupDeleteResponseModel}
   * @param {!number} prev Previous SymbolGroupDeleteResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SymbolGroupDeleteResponseModel };
